// src/UploadBuild.jsx
import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import { formatFileSize, gbToBytes } from "./utils/uploadLimits";

export default function UploadBuild({ gameId, onUploaded }) {
  const { currentUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [maxSizeGb, setMaxSizeGb] = useState(1);
  const [error, setError] = useState(null);

  const defaultName = "New Build";

  // Load user's subscription to get max upload size
  useEffect(() => {
    const loadMaxSize = async () => {
      if (!currentUser) return;
      try {
        const { data, error } = await supabase
          .from("subscriptions")
          .select(
            `
            subscription_plans ( max_build_size_gb )
          `
          )
          .eq("user_id", currentUser.id)
          .maybeSingle();

        if (error) {
          console.error("Error loading upload size limit:", error);
          setMaxSizeGb(1); // Default to free tier limit
        } else if (data?.subscription_plans?.max_build_size_gb) {
          setMaxSizeGb(data.subscription_plans.max_build_size_gb);
        } else {
          setMaxSizeGb(1); // Default to free tier
        }
      } catch (e) {
        console.error("Unexpected error loading upload size:", e);
        setMaxSizeGb(1);
      }
    };

    loadMaxSize();
  }, [currentUser]);

  const uploadFile = async (file) => {
    if (!file) return;
    
    // Clear previous error
    setError(null);

    // Check file size (convert GB to bytes)
    const maxSizeBytes = gbToBytes(maxSizeGb);
    if (file.size > maxSizeBytes) {
      setError(
        `File size (${formatFileSize(file.size)}) exceeds your plan limit of ${maxSizeGb} GB. Upgrade your plan for larger builds.`
      );
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("game_id", gameId);
      formData.append("build_name", defaultName);

      const { data, error } = await supabase.functions.invoke("ingest-zip", {
        body: formData,
      });
      if (error) throw error;

      setResult(data);
      if (onUploaded && data && data.build) {
        onUploaded(data.build);
      }
    } catch (err) {
      console.error(err);
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0] || null;
    if (f) await uploadFile(f);
    e.target.value = "";
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <input
          id="upload-build-input"
          type="file"
          accept=".zip"
          className="hidden"
          onChange={onFileChange}
        />

        <label
          htmlFor="upload-build-input"
          className={`px-3 py-2 inline-flex items-center justify-center rounded-lg border ${
            uploading
              ? "border-[#555] bg-[#333]"
              : "border-[#8338ec] bg-[#8338ec]/20 hover:bg-[#8338ec]"
          } text-sm cursor-pointer select-none ${error ? "opacity-50" : ""}`}
          aria-disabled={uploading || !!error}
        >
          {uploading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              Uploading…
            </>
          ) : (
            "Upload new build (.zip)"
          )}
        </label>
      </div>

      {/* Error message */}
      {error && (
        <div className="text-xs text-red-400 bg-red-900/30 border border-red-700 rounded px-3 py-2">
          {error}
        </div>
      )}

      {/* Size limit info */}
      {!error && (
        <div className="text-xs text-[#aaa]">
          Max file size: <span className="text-[#fff]">{maxSizeGb} GB</span>
        </div>
      )}

      {/* {result?.build?.entry_url && (
        <div className="text-sm text-green-300 ml-3 break-all">
          Published:{" "}
          <a
            className="underline"
            href={result.build.entry_url}
            target="_blank"
            rel="noreferrer"
          >
            open
          </a>
        </div>
      )} */}
    </div>
  );
}
